import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, BarChart3, Ticket, MousePointer, ThumbsUp, ThumbsDown, 
  TrendingUp, Award, Percent, Search, Trash2, AlertTriangle
} from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, title, value, subtitle, color = 'royal' }) => {
  const colorClasses = {
    royal: 'bg-royal/10 text-royal',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{title}</p>
          {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const AdminEstadisticas = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchHistory, setSearchHistory] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchSearchHistory();
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/search-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchHistory(response.data);
    } catch (error) {
      console.error('Error fetching search history:', error);
    }
  };

  const clearSearchHistory = async () => {
    if (!window.confirm('¿Borrar todo el historial de búsquedas IA?')) return;
    try {
      await axios.delete(`${API}/admin/search-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Historial de búsquedas borrado');
      fetchSearchHistory();
    } catch (error) {
      toast.error('Error al borrar el historial');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
    } catch (e) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="w-10 h-10 rounded-lg" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="admin-estadisticas">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin" className="p-2 bg-white rounded-lg shadow-sm hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </Link>
        <div>
          <h1 className="font-fredoka text-2xl font-bold text-slate-900">Estadísticas</h1>
          <p className="text-slate-600 text-sm">Métricas de rendimiento de tus cupones</p>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Ticket}
          title="Total Cupones"
          value={stats?.total_coupons || 0}
          color="royal"
        />
        <StatCard
          icon={TrendingUp}
          title="Cupones Activos"
          value={stats?.active_coupons || 0}
          color="emerald"
        />
        <StatCard
          icon={MousePointer}
          title="Total Clicks"
          value={stats?.total_clicks || 0}
          color="amber"
        />
        <StatCard
          icon={Percent}
          title="Confianza"
          value={`${stats?.trust_percentage || 0}%`}
          subtitle="Votos positivos"
          color="purple"
        />
      </div>

      {/* Votes Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          icon={ThumbsUp}
          title="Votos Positivos"
          value={stats?.total_worked || 0}
          subtitle="Me funcionó"
          color="emerald"
        />
        <StatCard
          icon={ThumbsDown}
          title="Votos Negativos"
          value={stats?.total_not_worked || 0}
          subtitle="No me funcionó"
          color="red"
        />
        <StatCard
          icon={Ticket}
          title="Cupones Expirados"
          value={stats?.expired_coupons || 0}
          color="red"
        />
      </div>

      {/* Top Coupons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top by Clicks */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
          <h2 className="font-fredoka text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-amber-500" />
            Top Cupones por Clicks
          </h2>
          <div className="space-y-3">
            {stats?.top_by_clicks?.length > 0 ? (
              stats.top_by_clicks.map((coupon, index) => (
                <div key={coupon.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-amber-500 text-white' :
                    index === 1 ? 'bg-slate-400 text-white' :
                    index === 2 ? 'bg-amber-700 text-white' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{coupon.title}</p>
                  </div>
                  <span className="text-sm font-bold text-amber-600">{coupon.clicks} clicks</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No hay datos aún</p>
            )}
          </div>
        </div>

        {/* Top by Votes */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
          <h2 className="font-fredoka text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-emerald-500" />
            Top Cupones por Votos
          </h2>
          <div className="space-y-3">
            {stats?.top_by_votes?.length > 0 ? (
              stats.top_by_votes.map((coupon, index) => (
                <div key={coupon.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-emerald-500 text-white' :
                    index === 1 ? 'bg-slate-400 text-white' :
                    index === 2 ? 'bg-emerald-700 text-white' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{coupon.title}</p>
                  </div>
                  <span className="text-sm font-bold text-emerald-600">{coupon.worked_count} votos</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No hay datos aún</p>
            )}
          </div>
        </div>
      </div>

      {/* AI Search History */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-100 p-4" data-testid="admin-search-history">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fredoka text-lg font-bold text-slate-900 flex items-center gap-2">
            <Search className="w-5 h-5 text-royal" />
            Búsquedas IA de tus usuarios
          </h2>
          {searchHistory?.items?.length > 0 && (
            <button
              onClick={clearSearchHistory}
              data-testid="clear-search-history-btn"
              className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" /> Borrar
            </button>
          )}
        </div>
        {searchHistory ? (
          <>
            {searchHistory.missing?.length > 0 && (
              <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="missing-offers-alert">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="font-bold text-amber-800 text-sm">
                    ¡Ofertas que te faltan por subir!
                  </p>
                </div>
                <p className="text-xs text-amber-700 mb-3">
                  Estos términos se buscaron 5 veces o más y no encontraron ninguna oferta. Considera subir productos así.
                </p>
                <div className="flex flex-wrap gap-2">
                  {searchHistory.missing.map((m, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-800 rounded-full px-3 py-1 text-xs font-semibold"
                      data-testid={`missing-term-${i}`}
                    >
                      {m.query}
                      <span className="bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5 text-[10px] font-bold">{m.count}×</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-4 mb-4 text-sm">
              <span className="text-slate-600">Total búsquedas: <b className="text-slate-900">{searchHistory.total_searches}</b></span>
              <span className="text-slate-600">Términos únicos: <b className="text-slate-900">{searchHistory.unique_terms}</b></span>
            </div>
            {searchHistory.items?.length > 0 ? (
              <div className="space-y-2">
                {searchHistory.items.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-2.5 rounded-lg ${item.zero_results > 0 ? 'bg-amber-50 border border-amber-100' : 'bg-slate-50'}`}
                    data-testid={`search-history-item-${index}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-royal/10 text-royal flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{item.query}</p>
                      <p className="text-xs text-slate-400">Última vez: {formatDate(item.last_at)}</p>
                    </div>
                    {item.zero_results > 0 && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap" title="Algunas veces no encontró ofertas — considera subir productos de este tipo">
                        Sin resultados ({item.zero_results})
                      </span>
                    )}
                    <span className="text-sm font-bold text-royal whitespace-nowrap">{item.count}×</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">Aún no hay búsquedas. Aparecerán aquí cuando tus usuarios usen el buscador inteligente.</p>
            )}
          </>
        ) : (
          <p className="text-slate-500 text-sm text-center py-4">Cargando historial...</p>
        )}
      </div>
    </div>
  );
};

export default AdminEstadisticas;
